import {
  ReactElement,
  MouseEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { t } from "@lingui/core/macro";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { TooltipWithBounds, useTooltip } from "@visx/tooltip";
import { bisectLeft } from "d3-array";
import { darken, desaturate } from "polished";

import {
  type Machine,
  type ThermalsDataCleaned,
  useMachineThermals,
} from "_/data/machines";
import { palette, defaultFontName } from "_/style";
import { formatTemperature } from "_/utils";
import { dateTimeString, timeOnlyString } from "_/utils/datetime";

import * as S from "./styled";

const MARGIN = { top: 16, right: 8, bottom: 48, left: 68 } as const;

// Minimum distance that must be dragged to be considered a valid drag selection.
const MIN_DRAG_PX = 10;

// Consider a data point a match only if it's within 3 seconds of the cursor.
// Beyond that the cursor is in a gap with no data.
const SNAP_THRESHOLD_MS = 3000;

// Default time window in seconds.
const DEFAULT_TIME_WINDOW = 60 * 60;

type SeriesKey = "bedTemp" | "chamberTemp" | "t0Temp" | "t1Temp";
type TargetKey = "bedTarget" | "chamberTarget" | "t0Target" | "t1Target";

const SERIES: ReadonlyArray<{ key: SeriesKey; color: string }> = [
  { key: "bedTemp", color: palette.paleBlue },
  { key: "chamberTemp", color: palette.tomatoRed },
  { key: "t0Temp", color: palette.kellyGreen },
  { key: "t1Temp", color: palette.pineappleYellow },
];

const TARGET_SERIES: ReadonlyArray<{ key: TargetKey; color: string }> = [
  { key: "bedTarget", color: desaturate(0.6, palette.paleBlue) },
  { key: "chamberTarget", color: desaturate(0.6, palette.tomatoRed) },
  { key: "t0Target", color: desaturate(0.6, palette.kellyGreen) },
  { key: "t1Target", color: desaturate(0.6, palette.pineappleYellow) },
];

type PathSet = Record<SeriesKey | TargetKey, string>;

type TooltipData = {
  timeMs: number;
  snapTimeMs: number | null;
  values: Record<SeriesKey, number | null>;
  targets: Record<SeriesKey, number | null>;
};

type ZoomDomain = { startMs: number; endMs: number };

type TemperatureChartProps = {
  machine: Machine;
  maxTemp?: number;
  timeWindow?: number;
};

function seriesLabel(key: SeriesKey): string {
  switch (key) {
    case "bedTemp":
      return t`common.bed`;
    case "chamberTemp":
      return t`common.chamber`;
    case "t0Temp":
      return t`common.left-tool`;
    case "t1Temp":
      return t`common.right-tool`;
  }
  throw new Error(`Unknown series key: ${String(key)}`);
}

// Builds an SVG path `d` string from parallel timestamp and value arrays.
// x = (timestamp_ms - epochMs) * scaleX  — keeps coordinates in [0, plotWidth] range
// while allowing the group transform to handle live panning without recomputing paths.
function buildLinePath(
  timestamps: number[],
  values: number[],
  epochMs: number,
  scaleX: number,
  toY: (v: number) => number
): string {
  const parts: string[] = [];
  let moveTo = true;

  for (let i = 0; i < timestamps.length; i++) {
    const val = values[i];

    if (val == null) {
      moveTo = true;
      continue;
    }

    const x = (timestamps[i] / 1000 - epochMs) * scaleX;
    const y = toY(val);
    parts.push(`${moveTo ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
    moveTo = false;
  }

  return parts.join("");
}

function computePaths(
  data: ThermalsDataCleaned,
  epochMs: number,
  scaleX: number,
  toY: (v: number) => number
): PathSet {
  return {
    bedTemp: buildLinePath(
      data.timestamp,
      data.bedTemperature,
      epochMs,
      scaleX,
      toY
    ),
    chamberTemp: buildLinePath(
      data.timestamp,
      data.chamberTemperature,
      epochMs,
      scaleX,
      toY
    ),
    t0Temp: buildLinePath(
      data.timestamp,
      data.t0Temperature,
      epochMs,
      scaleX,
      toY
    ),
    t1Temp: buildLinePath(
      data.timestamp,
      data.t1Temperature,
      epochMs,
      scaleX,
      toY
    ),
    bedTarget: buildLinePath(
      data.timestamp,
      data.bedTarget,
      epochMs,
      scaleX,
      toY
    ),
    chamberTarget: buildLinePath(
      data.timestamp,
      data.chamberTarget,
      epochMs,
      scaleX,
      toY
    ),
    t0Target: buildLinePath(
      data.timestamp,
      data.t0Target,
      epochMs,
      scaleX,
      toY
    ),
    t1Target: buildLinePath(
      data.timestamp,
      data.t1Target,
      epochMs,
      scaleX,
      toY
    ),
  };
}

export const TemperatureChart = ({
  machine,
  maxTemp = 450,
  timeWindow = DEFAULT_TIME_WINDOW,
}: TemperatureChartProps): ReactElement => {
  const { parentRef, width, height } = useParentSize();
  const clipPathId = useId();

  const timeWindowMs = timeWindow * 1000;

  // pixels per millisecond, for epoch-relative path coordinates
  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);
  const scaleX = plotWidth > 0 ? plotWidth / timeWindowMs : 0;

  // Mutable refs so the rAF loop never needs these in its effect deps (which would
  // cancel/restart the animation on every data tick). Written directly each render.
  const scaleXRef = useRef(scaleX);
  scaleXRef.current = scaleX;
  const plotWidthRef = useRef(plotWidth);
  plotWidthRef.current = plotWidth;
  const timeWindowMsRef = useRef(timeWindowMs);
  timeWindowMsRef.current = timeWindowMs;
  const epochMsRef = useRef(0);

  const [isLive, setIsLive] = useState(true);
  const [zoomDomain, setZoomDomain] = useState<ZoomDomain | null>(null);

  // Throttled at ~5fps in live mode to drive the time axis without excessive re-renders.
  const [axisXScale, setAxisXScale] = useState(() =>
    scaleTime({
      domain: [Date.now() - timeWindowMs, Date.now()],
      range: [0, plotWidth],
    })
  );

  const [pathSet, setPathSet] = useState<PathSet | null>(null);

  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragCurrentX, setDragCurrentX] = useState<number | null>(null);

  const dataRef = useRef<ThermalsDataCleaned | null>(null);
  const tsMsRef = useRef<number[]>([]);
  const pathsGroupRef = useRef<SVGGElement | null>(null);
  const rAFRef = useRef<number>(0);
  const lastAxisUpdateRef = useRef(0);

  // Memoized so path recomputation on resize uses the same scale instance.
  const yScale = useMemo(
    () =>
      scaleLinear({
        domain: [0, maxTemp],
        range: [plotHeight, 0],
        clamp: true,
      }),
    [maxTemp, plotHeight]
  );

  // Updated each render so it always captures current state values, but called via stable ref.
  const recomputePathsRef = useRef<(data: ThermalsDataCleaned) => void>(
    () => {}
  );
  recomputePathsRef.current = (data: ThermalsDataCleaned) => {
    if (isLive) {
      const now = Date.now();
      epochMsRef.current = now;
      setPathSet(computePaths(data, now, scaleX, yScale));
    } else if (zoomDomain) {
      const zScaleX =
        plotWidth > 0 ? plotWidth / (zoomDomain.endMs - zoomDomain.startMs) : 0;
      epochMsRef.current = zoomDomain.startMs;
      setPathSet(computePaths(data, zoomDomain.startMs, zScaleX, yScale));
    }
  };

  const stableOnData = useCallback((data: ThermalsDataCleaned) => {
    dataRef.current = data;
    tsMsRef.current = data.timestamp.map((ts) => ts / 1000);
    recomputePathsRef.current(data);
  }, []);

  useMachineThermals(machine, stableOnData);

  // Recompute paths on resize or maxTemp change
  useEffect(() => {
    if (dataRef.current) {
      recomputePathsRef.current(dataRef.current);
    }
  }, [plotWidth, plotHeight, maxTemp]);

  // rAF loop directly mutates the group transform and throttles axis scale React state to ~5fps.
  useEffect(() => {
    if (!isLive) return undefined;

    const pathsGroup = pathsGroupRef.current;

    const animate = () => {
      const now = Date.now();

      if (pathsGroupRef.current) {
        // Slide pre-computed paths so that 'now' aligns with the right edge of the plot.
        const dt = now - epochMsRef.current;
        const offsetX = plotWidthRef.current - dt * scaleXRef.current;
        pathsGroupRef.current.setAttribute(
          "transform",
          `translate(${offsetX.toFixed(2)},0)`
        );
      }

      // Throttle React re-renders: the axis ticks only need to update a few times/sec,
      // not at the full 60fps rAF rate.
      if (now - lastAxisUpdateRef.current > 200) {
        lastAxisUpdateRef.current = now;
        setAxisXScale(() =>
          scaleTime({
            domain: [now - timeWindowMsRef.current, now],
            range: [0, plotWidthRef.current],
          })
        );
      }

      rAFRef.current = requestAnimationFrame(animate);
    };

    rAFRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rAFRef.current);
      // Reset the transform so zoomed paths render at their natural coordinates.
      // Without this, the last live-mode translate() value stays on the DOM element.
      pathsGroup?.setAttribute("transform", "translate(0,0)");
    };
  }, [isLive]);

  const { hideTooltip, showTooltip, tooltipData, tooltipLeft, tooltipOpen } =
    useTooltip<TooltipData>();

  // Tracks the last hovered plot-x so the tooltip can be refreshed when new data arrives.
  const lastHoveredPxRef = useRef<number | null>(null);

  // Updated each render so it always captures the latest axisXScale and dataRef.
  const refreshTooltipRef = useRef<() => void>(() => {});
  refreshTooltipRef.current = () => {
    const px = lastHoveredPxRef.current;
    if (px == null || !dataRef.current) {
      return;
    }

    const timeMs = axisXScale.invert(px).getTime();
    const data = dataRef.current;
    const tsMs = tsMsRef.current;
    const insertionPt = bisectLeft(tsMs, timeMs);
    const idxBefore = Math.max(0, insertionPt - 1);
    const idxAfter = Math.min(tsMs.length - 1, insertionPt);
    const idx =
      Math.abs((tsMs[idxAfter] ?? Infinity) - timeMs) <
      Math.abs((tsMs[idxBefore] ?? Infinity) - timeMs)
        ? idxAfter
        : idxBefore;

    const hasData =
      tsMs.length > 0 &&
      Math.abs((tsMs[idx] ?? -Infinity) - timeMs) <= SNAP_THRESHOLD_MS;

    showTooltip({
      tooltipLeft: px + MARGIN.left,
      tooltipTop: 0,
      tooltipData: {
        timeMs,
        snapTimeMs: hasData ? tsMs[idx] : null,
        values: {
          bedTemp: hasData ? (data.bedTemperature[idx] ?? null) : null,
          chamberTemp: hasData ? (data.chamberTemperature[idx] ?? null) : null,
          t0Temp: hasData ? (data.t0Temperature[idx] ?? null) : null,
          t1Temp: hasData ? (data.t1Temperature[idx] ?? null) : null,
        },
        targets: {
          bedTemp: hasData ? (data.bedTarget[idx] ?? null) : null,
          chamberTemp: hasData ? (data.chamberTarget[idx] ?? null) : null,
          t0Temp: hasData ? (data.t0Target[idx] ?? null) : null,
          t1Temp: hasData ? (data.t1Target[idx] ?? null) : null,
        },
      },
    });
  };

  // Refresh the tooltip when new data arrives (pathSet changes) without waiting for mousemove.
  useEffect(() => {
    refreshTooltipRef.current();
  }, [pathSet]);

  function getPlotX(event: MouseEvent<SVGElement>): number | null {
    const coords = localPoint(event);
    if (!coords) return null;
    return coords.x - MARGIN.left;
  }

  function handleMouseMove(event: MouseEvent<SVGRectElement>) {
    const px = getPlotX(event);

    if (px != null) {
      lastHoveredPxRef.current = px;
    }

    refreshTooltipRef.current();

    if (dragStartX !== null) {
      setDragCurrentX(px);
    }
  }

  function handleMouseDown(event: MouseEvent<SVGRectElement>) {
    setDragStartX(getPlotX(event));
    setDragCurrentX(getPlotX(event));
  }

  function handleMouseUp() {
    if (dragStartX !== null && dragCurrentX !== null) {
      const left = Math.min(dragStartX, dragCurrentX);
      const right = Math.max(dragStartX, dragCurrentX);

      if (right - left >= MIN_DRAG_PX) {
        const startMs = axisXScale.invert(left).getTime();
        const endMs = axisXScale.invert(right).getTime();
        const newZoom: ZoomDomain = { startMs, endMs };

        setIsLive(false);
        setZoomDomain(newZoom);
        setAxisXScale(() =>
          scaleTime({ domain: [startMs, endMs], range: [0, plotWidth] })
        );

        if (dataRef.current) {
          const zScaleX = plotWidth > 0 ? plotWidth / (endMs - startMs) : 0;
          epochMsRef.current = startMs;
          setPathSet(computePaths(dataRef.current, startMs, zScaleX, yScale));
        }
      }
    }
    setDragStartX(null);
    setDragCurrentX(null);
  }

  function handleDoubleClick() {
    setIsLive(true);
    setZoomDomain(null);
    // Immediately recompute live paths — can't go through recomputePathsRef here because
    // it captures the pre-update state values (still has isLive=false, old zoomDomain).
    if (dataRef.current) {
      const now = Date.now();
      epochMsRef.current = now;
      setPathSet(computePaths(dataRef.current, now, scaleX, yScale));
      setAxisXScale(() =>
        scaleTime({ domain: [now - timeWindowMs, now], range: [0, plotWidth] })
      );
    }
  }

  function handleMouseLeave() {
    lastHoveredPxRef.current = null;
    hideTooltip();
    setDragStartX(null);
    setDragCurrentX(null);
  }

  const tickFont = `"${defaultFontName}", sans-serif`;

  const pathGroup = (
    <g ref={pathsGroupRef}>
      {pathSet &&
        TARGET_SERIES.map(({ key, color }) => (
          <path
            key={key}
            d={pathSet[key]}
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            fill="none"
          />
        ))}
      {pathSet &&
        SERIES.map(({ key, color }) => (
          <path
            key={key}
            d={pathSet[key]}
            stroke={color}
            strokeWidth={1.5}
            fill="none"
          />
        ))}
    </g>
  );

  const selectionRect =
    dragStartX !== null && dragCurrentX !== null ? (
      <rect
        x={Math.min(dragStartX, dragCurrentX)}
        y={0}
        width={Math.abs(dragCurrentX - dragStartX)}
        height={plotHeight}
        fill={palette.electricBlue}
        fillOpacity={0.15}
        stroke={palette.electricBlue}
        strokeWidth={1}
        pointerEvents="none"
      />
    ) : null;

  const cursorLine = tooltipOpen &&
    tooltipLeft != null &&
    dragStartX === null && (
      <line
        x1={tooltipLeft - MARGIN.left}
        x2={tooltipLeft - MARGIN.left}
        y1={0}
        y2={plotHeight}
        stroke={palette.ironGrey}
        strokeWidth={1}
        strokeDasharray="3 3"
        pointerEvents="none"
      />
    );

  const tooltip = tooltipOpen && tooltipData && (
    <TooltipWithBounds
      top={MARGIN.top + 8}
      left={tooltipLeft}
      style={{
        position: "absolute",
        pointerEvents: "none",
        background: "none",
        border: "none",
        boxShadow: "none",
        padding: 0,
      }}
    >
      <S.TooltipContent>
        <S.TooltipTime>
          {dateTimeString({
            value: tooltipData.timeMs,
            precision: "second",
          })}
        </S.TooltipTime>
        {SERIES.map(({ key, color }) => (
          <S.TooltipRow key={key} $color={color}>
            {seriesLabel(key)}
            {": "}
            <S.TooltipValue>
              {formatTemperature({
                value: tooltipData.values[key],
                kind: "current",
              })}
              {tooltipData.targets[key] != null && (
                <>
                  {" / "}
                  <S.TooltipSetpoint>
                    {formatTemperature({
                      value: tooltipData.targets[key],
                      kind: "target",
                    })}
                  </S.TooltipSetpoint>
                </>
              )}
            </S.TooltipValue>
          </S.TooltipRow>
        ))}
      </S.TooltipContent>
    </TooltipWithBounds>
  );

  const snapTimeMs = tooltipOpen ? (tooltipData?.snapTimeMs ?? null) : null;
  const cursorPoint =
    snapTimeMs != null &&
    tooltipData != null &&
    SERIES.map(({ key, color }) => {
      const value = tooltipData.values[key];
      if (value == null) return null;
      return (
        <circle
          key={key}
          cx={axisXScale(new Date(snapTimeMs)).toFixed(1)}
          cy={yScale(value).toFixed(1)}
          r={4}
          fill={darken(0.15, color)}
          pointerEvents="none"
        />
      );
    });

  return (
    <div ref={parentRef} style={{ height: "100%" }}>
      {plotWidth > 0 && plotHeight > 0 && (
        <svg width={width} height={height} style={{ display: "block" }}>
          <defs>
            <clipPath id={clipPathId}>
              <rect x={0} y={0} width={plotWidth} height={plotHeight} />
            </clipPath>
          </defs>

          <Group left={MARGIN.left} top={MARGIN.top}>
            <GridRows
              scale={yScale}
              width={plotWidth}
              stroke={palette.graniteBlack}
              strokeWidth={1}
            />

            <g clipPath={`url(#${clipPathId})`}>
              {pathGroup}
              {cursorPoint}
              {cursorLine}
              {selectionRect}
            </g>

            <AxisBottom
              scale={axisXScale}
              top={plotHeight}
              stroke={palette.ironGrey}
              tickStroke={palette.ironGrey}
              tickLabelProps={{
                fill: palette.ironGrey,
                fontSize: 11,
                fontFamily: tickFont,
                textAnchor: "middle",
              }}
              tickFormat={(d) =>
                timeOnlyString({
                  // We know it's a Date here.
                  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                  value: (d as Date).getTime(),
                  precision: "second",
                })
              }
            />

            <AxisLeft
              scale={yScale}
              stroke={palette.ironGrey}
              tickStroke={palette.ironGrey}
              label={t`common.temp-axis-label`}
              labelOffset={48}
              labelProps={{
                fill: palette.ironGrey,
                fontSize: 11,
                fontFamily: tickFont,
              }}
              tickLabelProps={{
                fill: palette.ironGrey,
                fontSize: 11,
                fontFamily: tickFont,
                textAnchor: "end",
                dx: "-0.25em",
                dy: "0.33em",
              }}
              tickFormat={(v) =>
                // We know it's a number here.
                // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                formatTemperature({ value: v as number, kind: "target" })
              }
            />

            <rect
              x={0}
              y={0}
              width={plotWidth}
              height={plotHeight}
              fill="transparent"
              style={{
                cursor: dragStartX !== null ? "ew-resize" : "crosshair",
              }}
              onMouseMove={handleMouseMove}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onDoubleClick={handleDoubleClick}
            />
          </Group>
        </svg>
      )}
      {tooltip}
    </div>
  );
};
